import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Grid, GridColumn } from 'js/design_system/grid/Grid';
import { Colors } from 'js/design_system/Color';
import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import { WorkSelectorUpload } from './components/WorkSelectorUpload';
import { CiterightContextProvider } from './CiterightContextProvider';
import {
  AnimatedLoadingStatus,
  CITATION_ANALYSIS_MESSAGES,
} from './components/AnimatedLoadingStatus';
import { ReportListModal } from './components/ReportListModal';
import { trackClick } from 'js/utils/trackClick';

// Type definitions
export interface Testimonial {
  text: string;
  source: string;
}

export interface FAQ {
  question: string;
  answer: string;
}

interface WorkAttachment {
  id: number;
  filename: string | null;
}

export interface UserWork {
  id: number;
  title: string;
  attachments_attributes?: WorkAttachment[];
}

interface CiterightLandingProps {
  userWorks?: UserWork[];
  testimonials?: Testimonial[];
  faqs?: FAQ[];
  isLoading?: boolean;
  onAnalyzeWork?: (workId: number) => void;
  onReportCreated?: (reportId: string) => void;
  /** Whether to show the "View Previous Reports" section (requires logged-in user) */
  showPreviousReports?: boolean;
}

const CiterightLandingContent: React.FC<CiterightLandingProps> = ({
  userWorks: userWorksProp,
  testimonials: testimonialsProp,
  faqs: faqsProp,
  isLoading = false,
  onAnalyzeWork,
  onReportCreated,
  showPreviousReports = false,
}) => {
  // Use null coalescing to handle both null and undefined from Rails
  const userWorks = userWorksProp ?? [];
  const testimonials = testimonialsProp ?? [];
  const faqs = faqsProp ?? [];
  const heroRef = React.useRef<HTMLDivElement>(null);
  const [showReportListModal, setShowReportListModal] = useState(false);
  const size = useBreakpoint();

  return (
    <Container>
      <HeroCard ref={heroRef}>
        <Grid>
          <GridColumn sm={0} md={0} lg={1} />
          <GridColumn sm={6} md={6} lg={10}>
            <FlexColumnCenter>
              <HeadingContainer>
                <H1Heading>
                  Find References In Minutes, Not&nbsp;Hours
                </H1Heading>

                <SubtitleText>
                  Get intelligent reference suggestions from verified academic
                  sources.
                  <br />
                  Try CiteRight for free now.
                </SubtitleText>
              </HeadingContainer>

              {isLoading ? (
                <LoadingContainer>
                  <AnimatedLoadingStatus
                    isLoading={true}
                    messages={CITATION_ANALYSIS_MESSAGES}
                    spinnerSize={32}
                  />
                </LoadingContainer>
              ) : (
                <WorkSelectorUpload
                  userWorks={userWorks}
                  importSource="landing"
                  onAnalyzeWork={onAnalyzeWork}
                  onReportCreated={onReportCreated}
                />
              )}
            </FlexColumnCenter>
          </GridColumn>
          <GridColumn sm={0} md={0} lg={1} />
        </Grid>
      </HeroCard>

      {showPreviousReports && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: Colors.GrayLight,
            padding: '24px 0',
            color: 'black',
          }}
        >
          <Grid>
            <GridColumn sm={6} md={6} lg={12}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                  flexDirection: size === 'small' ? 'column' : 'row',
                }}
              >
                <Body size="md">Looking for your previous reports?</Body>
                <Button
                  secondary
                  onClick={() => {
                    trackClick({ source: 'citeright.previous_reports_button' });
                    setShowReportListModal(true);
                  }}
                >
                  View previous reports
                </Button>
              </div>
            </GridColumn>
          </Grid>
        </div>
      )}

      <ReportListModal
        open={showReportListModal}
        onClose={() => setShowReportListModal(false)}
      />

      <TestimonialWrapper>
        <TestimonialContainer>
          <Grid>
            <GridColumn sm={0} md={0} lg={1}></GridColumn>
            <GridColumn sm={6} md={6} lg={10}>
              <TestimonialHeader>What Researchers Are Saying</TestimonialHeader>
              <TestimonialGrid>
                {testimonials.map((testimonial, index) => (
                  <TestimonialCard key={index}>
                    <TestimonialText>"{testimonial.text}"</TestimonialText>
                    <TestimonialSourceText>
                      {testimonial.source}
                    </TestimonialSourceText>
                  </TestimonialCard>
                ))}
              </TestimonialGrid>
            </GridColumn>
            <GridColumn sm={0} md={0} lg={1}></GridColumn>
          </Grid>
        </TestimonialContainer>
      </TestimonialWrapper>

      <FAQSection>
        <Grid>
          <GridColumn sm={0} md={0} lg={1} />
          <GridColumn sm={6} md={6} lg={10}>
            <FAQHeader>Frequently Asked Questions</FAQHeader>

            {faqs.map((faq, index) => (
              <React.Fragment key={index}>
                <FAQQuestionText>{faq.question}</FAQQuestionText>
                <FAQAnswerText>{faq.answer}</FAQAnswerText>
              </React.Fragment>
            ))}
          </GridColumn>
          <GridColumn sm={0} md={0} lg={1} />
        </Grid>
      </FAQSection>

      <Footer>
        <Grid>
          <GridColumn sm={6} md={4} lg={4}>
            <LogoWrapper>
              <img
                src="/images/design_system/academia_logo_white.svg"
                alt="Academia"
                width="140"
                height="24"
              />
            </LogoWrapper>
          </GridColumn>
          <GridColumn sm={3} md={4} lg={4}>
            <FooterAddress>
              580 California St., Suite 400
              <br />
              San Francisco, CA, 94104
            </FooterAddress>
          </GridColumn>
          <GridColumn sm={3} md={4} lg={4}>
            <FooterCopyright>
              © {new Date().getFullYear()} Academia. All rights reserved
            </FooterCopyright>
          </GridColumn>
        </Grid>
      </Footer>
    </Container>
  );
};

// Styled components
const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
`;

const H1Heading = styled(Heading).attrs({
  as: 'h1',
  size: 'xl',
  fontFamily: 'serif',
})`
  color: var(--text-primary, #141413);
  text-align: center;
`;

const SubtitleText = styled(Body).attrs({
  size: 'xl',
})`
  text-align: center;
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-xl-size, 24px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-xl-line-height, 36px);
`;

const FlexColumnCenter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-lg-80, 80px) 0px;
  gap: var(--spacing-md-40, 40px);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md-48, 48px) var(--spacing-sm-24, 24px);
  min-height: 200px;
`;

const Container = styled.div`
  font-family: 'DM Sans', sans-serif;
  height: 100vh;
  overflow-y: auto;
`;

const HeroCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  background: var(--background-beige-light);
`;

const TestimonialWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--columns-gutter, 48px);
`;

const TestimonialContainer = styled.div`
  display: flex;
  padding: var(--spacing-lg-80, 80px) 0px;
  justify-content: center;
  align-items: flex-start;
  gap: var(--columns-gutter, 48px);
`;

const TestimonialHeader = styled(Heading).attrs({
  as: 'h3',
  size: 'lg',
  fontFamily: 'serif',
})`
  color: var(--text-primary, #141413);
  text-align: center;
  font-family: var(--type-font-family-serif, Gupter);
  font-size: var(--type-serif-lg-size, 48px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-serif-lg-line-height, 56px);
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md-40, 40px);
  padding-top: var(--spacing-lg-64, 64px);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm-24, 24px);
  }
`;

const TestimonialCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24, 24px);
  background: var(--background-black, #141413);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--border-radius-md, 8px);
  padding: var(--spacing-md-32, 32px);
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.25);
`;

const TestimonialText = styled(Body).attrs({
  size: 'xs',
  fontFamily: 'serif',
})`
  color: var(--text-white, #fff);
  font-family: var(--type-font-family-serif, Gupter);
  font-size: var(--type-serif-xs-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-serif-xs-line-height, 20px);
`;

const TestimonialSourceText = styled(Body).attrs({
  size: 'sm',
})`
  color: var(--text-white, #fff);
  opacity: 0.8;
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-sm-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-sm-line-height, 20px);
`;

const FAQSection = styled.section`
  padding: var(--spacing-lg-80, 80px) 0;
  background: var(--background-white, #fff);
`;

const FAQHeader = styled(Heading).attrs({
  as: 'h2',
  size: 'lg',
  fontFamily: 'serif',
})`
  color: var(--text-primary, #141413);
  text-align: center;
  padding-bottom: var(--spacing-lg-64, 64px);
  font-family: var(--type-font-family-serif, Gupter);
  font-size: var(--type-serif-lg-size, 48px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-serif-lg-line-height, 56px);
`;

const FAQQuestionText = styled(Heading).attrs({
  as: 'h3',
  size: 'md',
  fontFamily: 'sans-serif',
})`
  color: var(--text-primary, #141413);
  --type-sans-serif-md-font-weight: 600;
  font-weight: 600;
  margin-top: var(--spacing-md-40, 40px);
  padding-bottom: var(--spacing-sm-24, 24px);
`;

const FAQAnswerText = styled(Body).attrs({
  size: 'md',
})`
  color: var(--text-primary, #141413);
  margin-bottom: var(--spacing-md-32, 32px);
  line-height: 1.6;
`;

const Footer = styled.footer`
  background-color: var(--background-black, #141413);
  width: 100%;
  padding: 40px 0;
  margin-top: 80px;
  position: relative;
  bottom: 80px;
`;

const FooterAddress = styled.address`
  color: var(--text-white, #ffffff);
  font-style: normal;
  font-size: var(--type-body-md-size, 16px);
  line-height: 1.5;
  padding: 24px;
  text-align: left;

  @media (max-width: 768px) {
    padding: 12px;
    font-size: var(--type-body-sm-size, 14px);
  }
`;

const FooterCopyright = styled.div`
  color: var(--text-white, #ffffff);
  font-size: var(--type-body-md-size, 16px);
  line-height: 1.5;
  padding: 24px;
  text-align: left;

  @media (max-width: 768px) {
    padding: 12px;
    font-size: var(--type-body-sm-size, 14px);
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const CiterightLanding: React.FC<CiterightLandingProps> = (props) => {
  return (
    <CiterightContextProvider>
      <CiterightLandingContent {...props} />
    </CiterightContextProvider>
  );
};

export default CiterightLanding;
